import { Component, inject } from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { DebugService } from '../../../services/debug.service';
import { TranslateModule } from '@ngx-translate/core';
import { GoodsReceiptArticleList } from '../../../../../api/addons-v3/models/goods-receipt-article-list';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'app-debug-article-compare-dialog',
    templateUrl: './debug-article-compare-dialog.component.html',
    styleUrl: './debug-article-compare-dialog.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatNativeDateModule,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        TranslateModule,
        MatTooltip,
    ],
})
export class DebugArticleCompareDialogComponent {
    compareForm: FormGroup;
    private fb = inject(FormBuilder);
    private dialogRef = inject(
        MatDialogRef<DebugArticleCompareDialogComponent>
    );
    private debugService = inject(DebugService);
    public data: DebugArticleCompareInputData = inject(MAT_DIALOG_DATA);

    constructor() {
        this.compareForm = this.fb.group({
            organizationSiteKey: this.fb.control(
                this.data.organizationSiteKey,
                [Validators.required]
            ),
            articles: this.fb.array([this.createArticleFormGroup()]),
        });
    }

    private createArticleFormGroup(): FormGroup {
        return this.fb.group({
            articleOrderNumber: ['', [Validators.required]],
            consigneeOrderNumber: ['', [Validators.required]],
            quantity: [1, [Validators.required, Validators.min(0)]],
        });
    }

    get articlesArray(): FormArray {
        return this.compareForm.get('articles') as FormArray;
    }

    addArticle(): void {
        this.articlesArray.push(this.createArticleFormGroup());
    }

    removeArticle(index: number): void {
        if (this.articlesArray.length > 1) {
            this.articlesArray.removeAt(index);
        }
    }

    async onSubmit(): Promise<void> {
        if (this.compareForm.invalid) {
            this.compareForm.markAllAsTouched();
            return;
        }

        const resultData = {
            organizationSiteKey: this.compareForm.get('organizationSiteKey')
                ?.value as string,
            articles: this.articlesArray.value as GoodsReceiptArticleList,
        } as DebugArticleCompareResultData;

        if (this.debugService.isLoggingEnabled()) {
            console.log(
                '[DebugService] Submitting article compare with payload:',
                resultData
            );
        }

        this.closeWithResult('success', resultData);
    }

    closeWithResult(
        result: string,
        data?: DebugArticleCompareResultData
    ): void {
        this.dialogRef.close({ result, data });
    }
}

export interface DebugArticleCompareInputData {
    organizationSiteKey: string;
}

export interface DebugArticleCompareResultData {
    organizationSiteKey: string;
    articles: GoodsReceiptArticleList;
}
